import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { assistantService } from '../api/services/assistant.service'
import { caseService } from '../api/services/case.service'
import { doctorService } from '../api/services/doctor.service'
import {
  buildCasePayload,
  caseToFormValues,
  CaseAllowNationalCommentsField,
  CaseAssistantReviewField,
  CaseFormFields,
  CaseNationalHubField,
  CaseReviewToggleField,
  useCaseForm,
} from '../components/cases/CaseFormFields'
import {
  CaseReviewerEditSection,
  reviewerIdsFromSlots,
  slotsFromReviewerIds,
} from '../components/cases/CaseReviewerEditSection'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { FieldLabel } from '../components/ui/field-label'
import { Input } from '../components/ui/input'
import { SelectPicker } from '../components/ui/select-picker'
import type { SelectOption } from '../components/ui/select-picker'
import { IconActionButton } from '../components/ui/table-icon-actions'
import { useAuth } from '../context/AuthContext'
import { canEditCaseOutcome } from '../lib/case-phase'
import { USER_ROLES } from '../types/auth'
import type { Case, CaseFiltersResponse, CaseReviewerPickerResponse } from '../types/case'

type StatusAction = 'keep' | 'publish' | 'schedule'

export function CaseEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { session, can } = useAuth()
  const role = session?.role ?? null

  const [caseItem, setCaseItem] = useState<Case | null>(null)
  const [filters, setFilters] = useState<CaseFiltersResponse | null>(null)
  const [doctorOptions, setDoctorOptions] = useState<SelectOption[]>([])
  const [reviewerPicker, setReviewerPicker] = useState<CaseReviewerPickerResponse | null>(null)
  const [reviewerSlots, setReviewerSlots] = useState<[string, string]>(['', ''])
  const [statusAction, setStatusAction] = useState<StatusAction>('keep')
  const [scheduleDate, setScheduleDate] = useState('')
  const [wantCloseReview, setWantCloseReview] = useState(false)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const form = useCaseForm()
  const showDoctorPicker = role === USER_ROLES.ASSISTANT || role === USER_ROLES.SUPER_ADMIN
  const consultantReadOnly = role === USER_ROLES.DOCTOR
  const isHardBlocked = Boolean(caseItem?.deleted_at || caseItem?.is_archived)
  const showOutcome = caseItem ? canEditCaseOutcome(caseItem) : false
  const isAssistant = role === USER_ROLES.ASSISTANT
  const sessionUserId = session?.user?.id
  /** Assistants may manage reviewers only on cases they uploaded (policy parity). */
  const assistantOwnsUpload = Boolean(
    isAssistant &&
      caseItem &&
      (caseItem.uploaded_by_user_id === sessionUserId ||
        caseItem.uploaded_by?.id === sessionUserId),
  )
  const canManageReviewers =
    can('cases-reviewers-sync') &&
    !isHardBlocked &&
    (!isAssistant || assistantOwnsUpload)
  /** Assistants create/edit drafts only — no publish/schedule status actions. */
  const canSetCaseStatus = !isAssistant && !isHardBlocked
  const canOpenReview = can('cases-open-review')
  const canCloseReview = can('cases-close-review')
  const isReviewClosed = Boolean(caseItem?.is_review_closed)
  const selectedReviewerCount = reviewerIdsFromSlots(reviewerSlots).length
  /**
   * Three review states (no "default" label in the UI):
   * - default: no reviewers selected → hide review card
   * - open: ≥1 reviewer selected → show Close Review checkbox
   * - closed: permanently closed → show Review Closed, lock reviewers
   */
  const reviewersLocked = isReviewClosed || wantCloseReview
  const showReviewClosedCard =
    isReviewClosed && !isAssistant && !isHardBlocked
  const showCloseReviewCard =
    !isReviewClosed &&
    selectedReviewerCount >= 1 &&
    !isAssistant &&
    !isHardBlocked &&
    (canCloseReview || canOpenReview)
  const showReviewField = showReviewClosedCard || showCloseReviewCard

  const canSetGlobal = useMemo(() => {
    if (role === USER_ROLES.ASSISTANT) {
      return false
    }

    if (role === USER_ROLES.SUPER_ADMIN || role === USER_ROLES.DOCTOR) {
      return true
    }

    return false
  }, [role])

  const showPublicField = canSetGlobal && !isAssistant
  const canOpenForAssistantReview =
    (role === USER_ROLES.DOCTOR || role === USER_ROLES.SUPER_ADMIN) && !isHardBlocked
  const showPublicReviewRow =
    showPublicField || showReviewField || canOpenForAssistantReview

  useEffect(() => {
    if (!canSetGlobal) {
      form.setValue('is_global', false)
    }
  }, [canSetGlobal, form])

  useEffect(() => {
    if (!id) {
      return
    }

    let cancelled = false
    setLoading(true)

    const load = async () => {
      try {
        const [caseResponse, filtersResponse] = await Promise.all([
          caseService.show(id),
          caseService.filters(),
        ])

        if (cancelled) {
          return
        }

        const loadedCase = caseResponse.data
        setCaseItem(loadedCase)
        setFilters(filtersResponse.data)
        form.reset(caseToFormValues(loadedCase))
        setScheduleDate(loadedCase.procedure_date ?? '')
        setWantCloseReview(false)

        const ownsUpload =
          loadedCase.uploaded_by_user_id === sessionUserId ||
          loadedCase.uploaded_by?.id === sessionUserId
        const shouldLoadReviewers =
          can('cases-reviewers-sync') && (!isAssistant || ownsUpload)

        if (!shouldLoadReviewers) {
          setReviewerSlots(['', ''])
          setReviewerPicker(null)
          return
        }

        const [reviewersResponse, pickerResponse] = await Promise.all([
          caseService.listReviewers(id).catch(() => ({ data: [] as const })),
          caseService
            .reviewerPicker(id)
            .catch(() => ({ data: { doctors: [], assistants: [] } })),
        ])

        if (cancelled) {
          return
        }

        const ids = (reviewersResponse.data ?? []).map((item) => item.user_id)
        setReviewerSlots(slotsFromReviewerIds(ids))
        setReviewerPicker(pickerResponse.data)
      } catch {
        if (!cancelled) {
          setCaseItem(null)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [can, form, id, isAssistant, sessionUserId])

  useEffect(() => {
    if (role === USER_ROLES.ASSISTANT) {
      void assistantService.doctorPicker().then((response) => {
        const options = (response.data ?? [])
          .map((link) => {
            const doctor = link.doctor
            if (!doctor) {
              return null
            }

            return {
              value: doctor.id,
              label: `${doctor.first_name} ${doctor.surname}`.trim() || doctor.email,
            }
          })
          .filter((option): option is SelectOption => option !== null)

        setDoctorOptions(options)
      })
      return
    }

    if (role === USER_ROLES.SUPER_ADMIN) {
      void doctorService.list({ status: 'approved' }).then((response) => {
        const options = (Array.isArray(response.data) ? response.data : []).map((doctor) => ({
          value: doctor.id,
          label: `${doctor.first_name} ${doctor.surname}`.trim() || doctor.email,
        }))
        setDoctorOptions(options)
      })
    }
  }, [role])

  const handleReviewerSlotsChange = (next: [string, string]) => {
    setReviewerSlots(next)
    // Clearing all reviewers returns the form to the default state (no Close Review card).
    if (reviewerIdsFromSlots(next).length === 0) {
      setWantCloseReview(false)
    }
  }

  const statusOptions = useMemo(() => {
    const options: SelectOption[] = [{ value: 'keep', label: `Keep as ${caseItem?.status ?? 'draft'}` }]
    if (can('cases-publish') && (caseItem?.status === 'draft' || caseItem?.status === 'scheduled')) {
      options.push({ value: 'publish', label: 'Publish' })
    }
    if (can('cases-schedule') && (caseItem?.status === 'draft' || caseItem?.status === 'scheduled')) {
      options.push({ value: 'schedule', label: 'Schedule' })
    }
    return options
  }, [can, caseItem?.status])

  const onSubmit = async (values: ReturnType<typeof caseToFormValues>) => {
    if (!id || isHardBlocked) {
      return
    }

    setSubmitting(true)

    try {
      const reviewerIds = reviewerIdsFromSlots(reviewerSlots)
      const wasUnderReview = Boolean(caseItem?.is_under_review)
      const wasReviewClosed = Boolean(caseItem?.is_review_closed)

      // Closed cases stay closed — reviewers are locked and cannot be changed.
      if (wasReviewClosed) {
        await caseService.update(id, buildCasePayload(values))
        if (canSetCaseStatus) {
          if (statusAction === 'publish' && can('cases-publish')) {
            await caseService.publish(id)
          } else if (statusAction === 'schedule' && can('cases-schedule')) {
            await caseService.schedule(id, {
              procedure_date: scheduleDate || values.procedure_date,
            })
          }
        }
        navigate(`/cases/${id}`)
        return
      }

      // ≥1 reviewer → open for review on save (unless permanently closing).
      const willCloseReview = wantCloseReview && canCloseReview && wasUnderReview
      const willOpenThenClose =
        wantCloseReview &&
        !wasUnderReview &&
        reviewerIds.length >= 1 &&
        canOpenReview &&
        canCloseReview
      const willOpenReview =
        !wantCloseReview &&
        !wasUnderReview &&
        reviewerIds.length >= 1 &&
        canOpenReview &&
        canManageReviewers
      // No reviewers + not closing → default state (clears under-review if needed).
      const willReturnToDefault =
        !wantCloseReview && wasUnderReview && reviewerIds.length === 0 && canManageReviewers

      await caseService.update(id, buildCasePayload(values))

      if (willOpenThenClose) {
        await caseService.openReview(id, { reviewer_ids: reviewerIds })
        await caseService.closeReview(id)
      } else if (willCloseReview) {
        if (canManageReviewers && reviewerIds.length >= 1) {
          await caseService.syncReviewers(id, { reviewer_ids: reviewerIds })
        }
        await caseService.closeReview(id)
      } else if (willReturnToDefault) {
        await caseService.syncReviewers(id, { reviewer_ids: [] })
      } else if (willOpenReview) {
        await caseService.openReview(id, { reviewer_ids: reviewerIds })
      } else if (canManageReviewers && wasUnderReview && reviewerIds.length >= 1) {
        await caseService.syncReviewers(id, { reviewer_ids: reviewerIds })
      } else if (canManageReviewers && !wasUnderReview && reviewerIds.length === 0) {
        await caseService.syncReviewers(id, { reviewer_ids: [] })
      }

      if (canSetCaseStatus) {
        if (statusAction === 'publish' && can('cases-publish')) {
          await caseService.publish(id)
        } else if (statusAction === 'schedule' && can('cases-schedule')) {
          await caseService.schedule(id, {
            procedure_date: scheduleDate || values.procedure_date,
          })
        }
      }

      navigate(`/cases/${id}`)
    } catch {
      // handled globally
    } finally {
      setSubmitting(false)
    }
  }

  const blockedMessage = useMemo(() => {
    if (caseItem?.deleted_at) {
      return 'This case has been deleted. Restore it before editing.'
    }

    if (caseItem?.is_archived) {
      return 'Archived cases are read-only.'
    }

    return null
  }, [caseItem?.deleted_at, caseItem?.is_archived])

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-2 py-2">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            {id && (
              <IconActionButton icon={ArrowLeft} label="Back to Case" to={`/cases/${id}`} />
            )}
            <div>
              <h1 className="font-display text-2xl font-bold">Edit case</h1>
              <p className="mt-1 text-sm text-ink-600">
                {caseItem?.reference ? `Reference: ${caseItem.reference}` : 'Update case details.'}
                {isAssistant ? ' Assistants save cases as drafts only.' : ''}
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-ink-500">Loading case…</p>
        ) : !caseItem ? (
          <p className="text-sm text-danger">Case not found.</p>
        ) : (
          <>
            {blockedMessage && (
              <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                {blockedMessage}
              </div>
            )}

            <section className="rounded-lg border border-parch-100 bg-white p-6 shadow-card">
              <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
                <CaseFormFields
                  form={form}
                  filters={filters}
                  doctorOptions={doctorOptions}
                  showDoctorPicker={showDoctorPicker}
                  consultantReadOnly={consultantReadOnly}
                  disabled={isHardBlocked}
                  showOutcome={showOutcome}
                />

                {(canManageReviewers || (isReviewClosed && !isAssistant)) && (
                  <CaseReviewerEditSection
                    picker={reviewerPicker}
                    reviewerIds={reviewerSlots}
                    onChange={handleReviewerSlotsChange}
                    disabled={submitting || reviewersLocked}
                  />
                )}

                {showPublicReviewRow ? (
                  <div className="space-y-4">
                    <div
                      className={`grid items-stretch gap-4 ${
                        [
                          showPublicField,
                          showReviewField,
                          canOpenForAssistantReview,
                        ].filter(Boolean).length > 1
                          ? 'md:grid-cols-2 xl:grid-cols-3'
                          : 'grid-cols-1'
                      }`}
                    >
                      {showPublicField ? (
                        <CaseNationalHubField
                          form={form}
                          disabled={isHardBlocked || isAssistant}
                          canSetGlobal
                        />
                      ) : null}
                      {showReviewField ? (
                        <CaseReviewToggleField
                          reviewClosed={showReviewClosedCard}
                          wantClose={wantCloseReview}
                          onWantClose={(next) => {
                            if (!canCloseReview || isReviewClosed) {
                              return
                            }
                            setWantCloseReview(next)
                          }}
                          disabled={submitting || isReviewClosed || !canCloseReview}
                          visible
                        />
                      ) : null}
                      <CaseAssistantReviewField
                        form={form}
                        disabled={isHardBlocked || submitting}
                        visible={canOpenForAssistantReview}
                      />
                    </div>
                    {showPublicField ? (
                      <CaseAllowNationalCommentsField
                        form={form}
                        disabled={isHardBlocked || isAssistant || submitting}
                      />
                    ) : null}
                  </div>
                ) : null}

                {canSetCaseStatus && statusOptions.length > 1 && (
                  <fieldset className="space-y-3 rounded-md border border-parch-100 p-4">
                    <legend className="px-1 text-sm font-semibold text-ink-800">Case status</legend>
                    <p className="text-xs text-ink-500">
                      Optionally publish or schedule when you save. Workflow actions are managed
                      here instead of the case view page.
                    </p>
                    <div>
                      <FieldLabel optional>Status action</FieldLabel>
                      <SelectPicker
                        options={statusOptions}
                        value={statusAction}
                        onChange={(value) => setStatusAction(value as StatusAction)}
                      />
                    </div>
                    {statusAction === 'schedule' && (
                      <div>
                        <FieldLabel required>Schedule procedure date</FieldLabel>
                        <Input
                          type="date"
                          value={scheduleDate}
                          onChange={(event) => setScheduleDate(event.target.value)}
                        />
                      </div>
                    )}
                  </fieldset>
                )}

                <div className="flex justify-end gap-2 border-t border-parch-100 pt-4">
                  <Button type="button" variant="secondary" asChild>
                    <Link to={id ? `/cases/${id}` : '/cases'}>Cancel</Link>
                  </Button>
                  <Button type="submit" disabled={submitting || isHardBlocked}>
                    {submitting ? 'Saving…' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </section>
          </>
        )}
      </div>
    </AppShell>
  )
}
